import { InputNumber } from "@/components/form-control/input-number";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LocalizedText } from "@/modules/parking-payment/components/localized-text";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarSelector } from "components/form-control";
import { useContext, useState } from "react";
import { FieldErrors, useForm, useWatch } from "react-hook-form";
import { ClockIcon, CloseIcon } from "@/components/icons";
import { CityContext } from "@/contexts/city-context-provider";
import {
  buildProviderParking,
  MINUTES_PER_DAY,
  MONTHS_OF_YEAR,
  ParkingRateFormValues,
  parkingRateSchema,
  toastError,
  toastSuccess,
} from "../parking-payment.const";
import { formatApiError } from "@/services/web/api-error";
import { getFeeConfigVersionLabel } from "../fee-config.const";
import { FeeConfigStatusEnum } from "../fee-config.type";
import { useGetFeeConfigs } from "../hooks/use-fee-config-query";
import { usePaidTimeRanges } from "../hooks/use-paid-time-ranges";
import {
  FreeSessionWindowTypeEnum,
  LocalizedString,
  PaidDayOfWeek,
  ParkingRateStatusEnum,
  ParkingRateType,
  PaymentZoneArea,
} from "../payment-zone-area.type";
import { PricingConfigurator } from "./pricing-configurator/pricing-configurator";
import { ProviderParkingSection } from "./provider-parking-section";
import { SectionDivider } from "./section-divider";
import {
  DEFAULT_LONG_PRICING_CONFIG,
  DEFAULT_PRICING_CONFIG,
  toMajorUnit,
  toMinorUnit,
} from "../pricing-configurator.const";
import { AnchorUnitEnum, RateTypeEnum } from "../pricing-configurator.types";

type ParkingRateModalProps = {
  selectedRate: ParkingRateType | null;
  onClose: () => void;
  paymentZones: PaymentZoneArea[];
  onSubmit: (data: ParkingRateType) => Promise<void>;
  readOnly?: boolean;
};

const NO_FEE_CONFIG_VALUE = "none";

export const ParkingRateModal = ({
  selectedRate,
  onClose,
  paymentZones,
  onSubmit,
  readOnly = false,
}: ParkingRateModalProps) => {
  const { currentCity } = useContext(CityContext);
  const isEditMode = !!selectedRate?.id;

  const { feeConfigs } = useGetFeeConfigs(currentCity?.id ?? "");
  const enabledFeeConfigs = feeConfigs.filter((config) => config.status === FeeConfigStatusEnum.ENABLED);
  const referencedFeeConfig = selectedRate?.feeConfigId
    ? feeConfigs.find((config) => config.id === selectedRate.feeConfigId)
    : undefined;
  const staleFeeConfig =
    referencedFeeConfig && referencedFeeConfig.status !== FeeConfigStatusEnum.ENABLED ? referencedFeeConfig : undefined;

  const {
    generationMode,
    timeStep,
    anchors: defaultAnchors,
    roundTo,
    roundMode,
    keepAnchorsExact,
  } = DEFAULT_PRICING_CONFIG;

  const form = useForm<ParkingRateFormValues>({
    resolver: zodResolver(parkingRateSchema),
    defaultValues: {
      name: selectedRate?.name || [],
      detail: selectedRate?.detail || [],
      zoneId: selectedRate?.zoneId || "",
      startDate: selectedRate?.startDate ? new Date(selectedRate.startDate) : undefined,
      endDate: selectedRate?.endDate ? new Date(selectedRate.endDate) : null,
      shortcuts: selectedRate?.shortcuts || [],
      freeMonths: selectedRate?.freeMonths || [],
      freeOnHolidays: selectedRate?.freeOnHolidays || false,
      freeSession: selectedRate?.freeSession,
      extendable: selectedRate?.extendable ?? true,
      stoppable: selectedRate?.stoppable ?? true,
      rateAmount: selectedRate?.rateAmount ? toMajorUnit(selectedRate.rateAmount) : 0,
      rateUnit: selectedRate?.rateUnit || "",
      userProfile: selectedRate?.userProfile || "",
      feeConfigId: selectedRate?.feeConfigId ?? null,
      pricingConfiguration: selectedRate?.pricingConfiguration
        ? {
            ...selectedRate.pricingConfiguration,
            anchors: selectedRate.pricingConfiguration.anchors?.map((a) => ({
              ...a,
              id: a.id ?? crypto.randomUUID(),
              isShortcut:
                a.isShortcut ??
                selectedRate.shortcuts?.some(
                  (s) => s.duration === a.duration && s.unit === (a.unit ?? AnchorUnitEnum.MIN),
                ) ??
                false,
            })),
          }
        : {
            generationMode: { mode: generationMode, stepValue: timeStep },
            anchors: defaultAnchors.map((a) => ({
              ...a,
              id: crypto.randomUUID(),
              priceAmount: toMinorUnit(a.priceAmount),
            })),
            roundingRules: { roundTo, roundMode, keepAnchorsExact },
          },
      paidTimeRanges: selectedRate?.paidTimeRanges || [],
      rateType: selectedRate?.rateType ?? RateTypeEnum.SHORT,
      providerParking: selectedRate?.providerParking ?? {},
    },
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  const [activeTab, setActiveTab] = useState<string>("tarification");

  const handleInvalid = (formErrors: FieldErrors<ParkingRateFormValues>) => {
    const keys = Object.keys(formErrors);
    const isConfigKey = (k: string) => k === "pricingConfiguration" || k === "shortcuts";
    const isProviderKey = (k: string) => k === "providerParking";
    if (keys.some((k) => !isConfigKey(k) && !isProviderKey(k))) {
      setActiveTab("tarification");
    } else if (keys.some(isConfigKey)) {
      setActiveTab("configuration");
    } else {
      setActiveTab("fournisseur");
    }
    toastError("Veuillez corriger les erreurs avant de soumettre.");
  };
  const rateType = useWatch({ control, name: "rateType" });

  const freeSession = useWatch({ control, name: "freeSession" });
  const freeSessionLimitMode =
    freeSession?.periodMinutes == null && !freeSession?.windowType
      ? "session"
      : freeSession?.windowType === FreeSessionWindowTypeEnum.Calendar
        ? "calendar"
        : "rolling";
  const freeSessionDraftLocked =
    isEditMode && selectedRate?.status === ParkingRateStatusEnum.DRAFT && !!selectedRate?.freeSession;

  const handleFreeSessionLimitChange = (mode: string) => {
    const current = form.getValues("freeSession");
    if (!current) return;
    if (mode === "session") {
      // setValue merges object keys, so the removed fields must be cleared explicitly
      setValue("freeSession", { minutes: current.minutes, periodMinutes: undefined, windowType: undefined });
    } else {
      setValue("freeSession", {
        minutes: current.minutes,
        periodMinutes: current.periodMinutes ?? MINUTES_PER_DAY,
        windowType: mode === "calendar" ? FreeSessionWindowTypeEnum.Calendar : FreeSessionWindowTypeEnum.Rolling,
      });
    }
  };

  const [pendingRateType, setPendingRateType] = useState<RateTypeEnum | null>(null);

  const applyRateTypeChange = (newType: RateTypeEnum) => {
    const defaults = newType === RateTypeEnum.LONG ? DEFAULT_LONG_PRICING_CONFIG : DEFAULT_PRICING_CONFIG;
    const currentConfig = form.getValues("pricingConfiguration")!;
    setValue("pricingConfiguration", {
      ...currentConfig,
      anchors: defaults.anchors.map((a) => ({
        ...a,
        id: crypto.randomUUID(),
        priceAmount: toMinorUnit(a.priceAmount),
      })),
    });
    if (newType === RateTypeEnum.LONG) {
      setValue("freeSession", undefined);
    }
    setValue("rateType", newType);
  };

  const handleRateTypeChange = (newType: RateTypeEnum) => {
    if (newType === rateType) return;
    if (newType === RateTypeEnum.LONG && freeSessionDraftLocked) {
      toastError(
        "Ce brouillon a une session gratuite, qui n'existe que pour les tarifications courte durée et ne peut pas encore être retirée.",
      );
      return;
    }
    const currentAnchors = form.getValues("pricingConfiguration")?.anchors ?? [];
    if (currentAnchors.length > 0) {
      setPendingRateType(newType);
    } else {
      applyRateTypeChange(newType);
    }
  };

  const paidTimeRangesValue = useWatch({ control, name: "paidTimeRanges" }) ?? [];
  const paidTimeRanges = usePaidTimeRanges(paidTimeRangesValue, (ranges) =>
    setValue("paidTimeRanges", ranges, { shouldValidate: true }),
  );

  const save = async (data: ParkingRateFormValues) => {
    const { providerParking, ...rateValues } = data;
    const parkingRateData: ParkingRateType = {
      ...rateValues,
      ...(selectedRate?.id && { id: selectedRate.id }),
      cityId: currentCity!.id,
      zoneName: paymentZones.find((z) => z.id === data.zoneId)?.name ?? "",
      startDate: data.startDate.toISOString(),
      endDate: data.endDate ? data.endDate.toISOString() : null,
      rateAmount: toMinorUnit(data.rateAmount),
      feeConfigId: data.feeConfigId ?? null,
      pricingConfiguration: data.pricingConfiguration
        ? {
            ...data.pricingConfiguration,
            anchors: data.pricingConfiguration.anchors?.map(({ duration, unit, priceAmount }) => ({
              duration,
              unit,
              priceAmount,
            })),
          }
        : data.pricingConfiguration,
      paidTimeRanges: data.paidTimeRanges ?? [],
      freeMonths: data.freeMonths && data.freeMonths.length > 0 ? data.freeMonths : undefined,
      providerParking: buildProviderParking(providerParking),
    };

    try {
      await onSubmit(parkingRateData);
      toastSuccess(
        isEditMode ? "La tarification a été modifiée avec succès." : "La tarification a été créée avec succès.",
      );
      onClose();
    } catch (error) {
      toastError(formatApiError(error, "Une erreur est survenue lors de la sauvegarde de la tarification."));
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent aria-describedby={undefined} className="flex max-h-[90vh] flex-col p-0 sm:max-w-3xl">
          <Form {...form}>
            <form onSubmit={handleSubmit(save, handleInvalid)} className="flex flex-col flex-1 min-h-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                <div className="px-6 pt-6 pb-0">
                  <DialogHeader className="mb-4">
                    <DialogTitle>
                      {readOnly
                        ? "Détails de la tarification"
                        : isEditMode
                          ? "Modifier la tarification"
                          : "Nouvelle tarification"}
                    </DialogTitle>
                    <DialogDescription>Configurez les informations et les prix de la tarification.</DialogDescription>
                  </DialogHeader>
                  <TabsList>
                    <TabsTrigger value="tarification">Tarification</TabsTrigger>
                    <TabsTrigger value="configuration">Configuration des prix</TabsTrigger>
                    <TabsTrigger value="fournisseur">Fournisseur</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="tarification" className="flex-1 overflow-y-auto scrollbar-hide mt-0 px-6 pt-6">
                  <fieldset disabled={readOnly} className="space-y-6 pb-4">
                    <SectionDivider label="Informations générales" />

                    <FormField
                      control={control}
                      name="name"
                      render={({ field: { value, onChange } }) => (
                        <FormItem>
                          <FormLabel>
                            Nom <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <LocalizedText value={(value as LocalizedString[]) ?? []} onChange={onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="detail"
                      render={({ field: { value, onChange } }) => (
                        <FormItem>
                          <FormLabel>
                            Détail <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <LocalizedText value={(value as LocalizedString[]) ?? []} onChange={onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="zoneId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Zone <span className="text-red-500">*</span>
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value || undefined}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Sélectionnez une zone" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {paymentZones.length === 0 ? (
                                  <SelectItem value="_none" disabled>
                                    Aucune zone disponible
                                  </SelectItem>
                                ) : (
                                  paymentZones.map((zone) => (
                                    <SelectItem key={zone.id} value={zone.id}>
                                      {zone.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="userProfile"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Profil utilisateur <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Ex. Visiteur" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* ── Période ── */}
                    <SectionDivider label="Période" />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="startDate"
                        render={({ field: { value, onChange } }) => (
                          <FormItem>
                            <FormLabel>
                              Date de début <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <CalendarSelector selectedDate={value} onChangeValue={onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="endDate"
                        render={({ field: { value, onChange } }) => (
                          <FormItem>
                            <FormLabel>Date de fin</FormLabel>
                            <FormControl>
                              <CalendarSelector selectedDate={value ?? undefined} onChangeValue={onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* ── Tarif ── */}
                    <SectionDivider label="Tarif" />

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={control}
                        name="rateAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Montant (€) <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <InputNumber {...field} min={0} step={1} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="rateUnit"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Unité <span className="text-red-500">*</span>
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Sélectionnez une unité" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="heure">Heure</SelectItem>
                                <SelectItem value="journée">Journée</SelectItem>
                                <SelectItem value="semaine">Semaine</SelectItem>
                                <SelectItem value="mois">Mois</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <SectionDivider label="Commission" />

                    <FormField
                      control={control}
                      name="feeConfigId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Commission appliquée</FormLabel>
                          <Select
                            onValueChange={(value) => field.onChange(value === NO_FEE_CONFIG_VALUE ? null : value)}
                            value={field.value ?? NO_FEE_CONFIG_VALUE}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={NO_FEE_CONFIG_VALUE}>Aucune commission</SelectItem>
                              {enabledFeeConfigs.map((config) => (
                                <SelectItem key={config.id} value={config.id}>
                                  {config.name} ({getFeeConfigVersionLabel(config)})
                                </SelectItem>
                              ))}
                              {staleFeeConfig && (
                                <SelectItem value={staleFeeConfig.id}>
                                  {staleFeeConfig.name} ({getFeeConfigVersionLabel(staleFeeConfig)}) — désactivé
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Seules les versions actives sont proposées. Sans commission sélectionnée, aucune commission
                            n'est appliquée à cette tarification.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <SectionDivider label="Gratuité" />

                    <FormField
                      control={control}
                      name="freeOnHolidays"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={value} onCheckedChange={onChange} />
                          </FormControl>
                          <FormLabel className="font-normal">Gratuit les jours fériés</FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="freeMonths"
                      render={({ field: { value = [], onChange } }) => (
                        <FormItem>
                          <FormLabel>Mois gratuits</FormLabel>
                          <p className="text-xs text-muted-foreground">Mois pendant lesquels ce tarif est gratuit.</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {MONTHS_OF_YEAR.map(({ num, label }) => {
                              const isSelected = value.includes(num);
                              return (
                                <button
                                  key={num}
                                  type="button"
                                  aria-pressed={isSelected}
                                  aria-label={`Mois de ${label}`}
                                  onClick={() =>
                                    onChange(isSelected ? value.filter((m) => m !== num) : [...value, num])
                                  }
                                  className={cn(
                                    "px-3 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                    isSelected
                                      ? "bg-primary text-primary-foreground border-primary"
                                      : "bg-background text-muted-foreground border-border hover:border-primary/60 hover:text-foreground",
                                  )}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {rateType !== RateTypeEnum.LONG && (
                      <>
                        <FormField
                          control={control}
                          name="freeSession"
                          render={({ field: { value, onChange } }) => (
                            <FormItem>
                              <div className="flex flex-row items-center space-x-3">
                                <FormControl>
                                  <Checkbox
                                    checked={!!value}
                                    disabled={freeSessionDraftLocked}
                                    onCheckedChange={(checked) =>
                                      onChange(
                                        checked
                                          ? {
                                              minutes: 30,
                                              periodMinutes: MINUTES_PER_DAY,
                                              windowType: FreeSessionWindowTypeEnum.Calendar,
                                            }
                                          : undefined,
                                      )
                                    }
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  Session gratuite en début de stationnement
                                </FormLabel>
                              </div>
                              {freeSessionDraftLocked && (
                                <p className="text-xs text-muted-foreground">
                                  La session gratuite d'un brouillon ne peut pas encore être retirée ; seuls ses
                                  paramètres peuvent être modifiés.
                                </p>
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {freeSession && (
                          <div className="space-y-2 pl-7">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                              <FormField
                                control={control}
                                name="freeSession.minutes"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Minutes gratuites <span className="text-red-500">*</span>
                                    </FormLabel>
                                    <FormControl>
                                      <InputNumber {...field} min={1} step={1} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormItem>
                                <FormLabel>Renouvellement</FormLabel>
                                <Select value={freeSessionLimitMode} onValueChange={handleFreeSessionLimitChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="calendar">Une fois par période calendaire</SelectItem>
                                    <SelectItem value="rolling">Une fois par période glissante</SelectItem>
                                    <SelectItem value="session">À chaque session</SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>

                              {freeSessionLimitMode !== "session" && (
                                <FormField
                                  control={control}
                                  name="freeSession.periodMinutes"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        Période (min) <span className="text-red-500">*</span>
                                      </FormLabel>
                                      <FormControl>
                                        <InputNumber {...field} min={2} step={1} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Les premières minutes de la session sont gratuites, accordées au plus une fois par période
                              et par plaque. Période calendaire : créneaux alignés sur minuit, la durée doit diviser 24
                              h (1440 min = une fois par jour). Période glissante : une fois toutes les X minutes. Ex. :
                              30 min / 1440 / calendaire, ou 20 min / 360 / glissante.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    <SectionDivider label="Options ticket" />

                    <FormField
                      control={control}
                      name="extendable"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={value} onCheckedChange={onChange} />
                          </FormControl>
                          <FormLabel className="font-normal">Prolongeable</FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="stoppable"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={value} onCheckedChange={onChange} />
                          </FormControl>
                          <FormLabel className="font-normal">Arrêtable</FormLabel>
                        </FormItem>
                      )}
                    />

                    {/* ── Plages horaires ── */}
                    <SectionDivider label="Plages horaires" />

                    <FormField
                      control={control}
                      name="paidTimeRanges"
                      render={() => {
                        const {
                          applyShortcut,
                          addDay,
                          updateRange,
                          removeRange,
                          availableDays,
                          getDayLabel,
                          shortcuts,
                        } = paidTimeRanges;

                        return (
                          <FormItem>
                            <div className="flex flex-wrap gap-2">
                              {shortcuts.map((shortcut) => (
                                <Button
                                  key={shortcut.label}
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => applyShortcut([...shortcut.ranges])}
                                >
                                  {shortcut.label} 9h–19h
                                </Button>
                              ))}
                            </div>

                            {paidTimeRangesValue.length === 0 ? (
                              <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30 px-4 py-3 mt-2 w-fit">
                                <ClockIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                                <div>
                                  <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                                    Actif 24h/24 · 7j/7
                                  </p>
                                  <p className="text-xs text-emerald-600/80 dark:text-emerald-400/70">
                                    Aucune restriction horaire — ce tarif s'applique en permanence.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 space-y-0">
                                {paidTimeRangesValue.map((range, index) => {
                                  const isFirstOfDay = index === 0 || paidTimeRangesValue[index - 1].day !== range.day;
                                  const isLastOfDay =
                                    index === paidTimeRangesValue.length - 1 ||
                                    paidTimeRangesValue[index + 1].day !== range.day;
                                  const rowError =
                                    errors.paidTimeRanges?.[index]?.endTime?.message ??
                                    errors.paidTimeRanges?.[index]?.startTime?.message;
                                  return (
                                    <div
                                      key={`${range.day}-${index}`}
                                      className={
                                        isFirstOfDay && index !== 0 ? "pt-3 border-t border-border/50 mt-3" : ""
                                      }
                                    >
                                      <div className="flex items-center gap-2 py-0.5">
                                        <span className="w-24 text-sm font-medium">
                                          {isFirstOfDay ? getDayLabel(range.day) : ""}
                                        </span>
                                        <input
                                          type="time"
                                          value={range.startTime}
                                          onChange={(e) => updateRange(index, "startTime", e.target.value)}
                                          className="h-8 rounded-md border border-input bg-background px-2 text-sm w-28"
                                        />
                                        <span className="text-muted-foreground text-xs">→</span>
                                        <input
                                          type="time"
                                          value={range.endTime}
                                          onChange={(e) => updateRange(index, "endTime", e.target.value)}
                                          className="h-8 rounded-md border border-input bg-background px-2 text-sm w-28"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => removeRange(index)}
                                          className="text-muted-foreground hover:text-destructive px-2"
                                        >
                                          <CloseIcon className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      {rowError && (
                                        <p className="pl-[calc(6rem+0.5rem)] text-xs text-destructive">{rowError}</p>
                                      )}
                                      {isLastOfDay && (
                                        <button
                                          type="button"
                                          onClick={() => addDay(range.day)}
                                          className="ml-[calc(6rem+0.5rem)] mt-0.5 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                                        >
                                          <span className="text-base leading-none">+</span> plage
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {availableDays.length > 0 && (
                              <Select onValueChange={(v) => addDay(v as PaidDayOfWeek)}>
                                <SelectTrigger className="mt-2 w-48">
                                  <SelectValue placeholder="+ Ajouter un jour" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableDays.map((d) => (
                                    <SelectItem key={d.value} value={d.value}>
                                      {d.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}

                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </fieldset>
                </TabsContent>

                <TabsContent value="configuration" className="flex-1 overflow-y-auto mt-0 px-6 pt-6">
                  <fieldset disabled={readOnly} className="pb-4 space-y-6">
                    <FormField
                      control={control}
                      name="rateType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type de tarif</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={(value) => handleRateTypeChange(value as RateTypeEnum)}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={RateTypeEnum.SHORT}>Courte durée (visiteur)</SelectItem>
                              <SelectItem value={RateTypeEnum.LONG}>Longue durée (abonné)</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="pricingConfiguration"
                      render={() => (
                        <FormItem>
                          <PricingConfigurator
                            control={control}
                            setValue={setValue}
                            rateType={rateType ?? RateTypeEnum.SHORT}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </fieldset>
                </TabsContent>

                <TabsContent value="fournisseur" className="flex-1 overflow-y-auto scrollbar-hide mt-0 px-6 pt-6">
                  <fieldset disabled={readOnly} className="space-y-6 pb-4">
                    <SectionDivider label="Identifiants fournisseur" />
                    <ProviderParkingSection control={control} setValue={setValue} cityId={currentCity?.id ?? ""} />
                  </fieldset>
                </TabsContent>
              </Tabs>

              <div className="px-6 mt-4 py-4 pt-2">
                <DialogFooter className={readOnly ? "sm:justify-end" : "sm:justify-between"}>
                  {!readOnly && (
                    <Button variant="secondary" onClick={onClose} type="button">
                      Annuler
                    </Button>
                  )}
                  {readOnly ? (
                    <Button variant="secondary" onClick={onClose} type="button">
                      Fermer
                    </Button>
                  ) : (
                    <Button disabled={isSubmitting} type="submit">
                      {isEditMode ? "Modifier" : "Créer la tarification"}
                    </Button>
                  )}
                </DialogFooter>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingRateType !== null} onOpenChange={() => setPendingRateType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le type de tarification</DialogTitle>
            <DialogDescription>
              {pendingRateType === RateTypeEnum.LONG && freeSession
                ? "Changer le type effacera vos points d'ancrage actuels ainsi que la session gratuite. Cette action est irréversible."
                : "Changer le type effacera vos points d'ancrage actuels. Cette action est irréversible."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPendingRateType(null)}>
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (pendingRateType) applyRateTypeChange(pendingRateType);
                setPendingRateType(null);
              }}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
